import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import {
  RouteGuard,
  RouteGuardState,
  useRouteGuard,
} from "@/components/runtime/route-guard/RouteGuard";
import { ListSkeleton } from '@/components/skeletons/SkeletonLayouts';

interface AuthGateProps {
  children: JSX.Element;
}

/**
 * Гард для страниц, требующих аутентификации
 * Не редиректит пока статус loading, предотвращая петли
 */
const GuardSkeleton = ({ message }: { message: string }) => (
  <div className="min-h-screen flex items-center justify-center px-4">
    <div className="w-full max-w-md space-y-4 text-center" role="status" aria-live="polite">
      <ListSkeleton items={4} />
      <p className="text-sm text-muted-foreground">{message}</p>
    </div>
  </div>
);

export function RequireAuth({ children }: AuthGateProps) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const loadingMessage = t('loading', 'Loading...');
  if (typeof window !== 'undefined' && window.localStorage.getItem('e2e_dashboard_stub') === 'true') {
    return children;
  }
  const renderLoading = (state: RouteGuardState) =>
    state.view ?? (
      <GuardSkeleton message={loadingMessage} />
    );

  const renderBlocked = (state: RouteGuardState) => {
    const isStaging = import.meta.env.VITE_ENV_LABEL === "STG";
    const redirectTo = isStaging ? "/user-access-denied" : state.meta.redirectTo ?? "/auth";
    return <Navigate to={redirectTo} replace state={{ from: location }} />;
  };

  return (
    <RouteGuard mode="customer" renderLoading={renderLoading} renderBlocked={renderBlocked}>
      {children}
    </RouteGuard>
  );
}

/**
 * Гард для страниц, требующих отсутствия аутентификации (auth, landing)
 * Не редиректит пока статус loading
 * 
 * CANON v1.3.0: Authenticated users redirect to /dashboard (NOT /verify).
 * /verify is a legacy transitional route - see KYC_REDIRECT_FLOW_ANALYSIS.md
 */
export function RequireGuest({ children }: AuthGateProps) {
  const guard = useRouteGuard("customer");
  const { t } = useTranslation('common');
  const loadingMessage = t('loading', 'Loading...');

  if (guard.status === "loading") {
    return (
      guard.view ?? (
        <GuardSkeleton message={loadingMessage} />
      )
    );
  }

  if (guard.meta.authed) {
    // CANON v1.3.0: Redirect to /dashboard, NOT /verify
    // Dashboard is the canonical source of truth for user status
    // OnboardingWizard handles new user flows via /onboarding/wizard
    return <Navigate to="/dashboard" replace />;
  }

  return children;
}

/**
 * Опциональный гард - пропускает всех, но предоставляет loading состояние
 */
export function OptionalAuth({ children }: AuthGateProps) {
  const guard = useRouteGuard("customer");
  const { t } = useTranslation('common');
  const loadingMessage = t('loading', 'Loading...');

  if (guard.status === "loading") {
    return (
      guard.view ?? (
        <GuardSkeleton message={loadingMessage} />
      )
    );
  }

  return children;
}